import {capitalize} from 'lodash';
import {useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import {unwrapResult} from '@reduxjs/toolkit';
import React, {useMemo, useEffect, useCallback, useState} from 'react';

import {DataSet} from '../model/DataSetDto';
import {Status} from '../../../types/operation/Status';
import {RootState, useAppDispatch} from '../../../store';
import {addListener, removeListener} from '../../../store/statusChecker';
import {DataPreparationOrigin} from '../../../types/dataset/DataPreparationContainer';
import TimerWaiter from '../../../base-components/StudioTimerWaiter/StudioTimerWaiter';
import {
  DataPrepareType,
  DataPrepareTypeStr,
} from '../../../types/dataset/DatasetGetAnnotationsResponse';
import {
  loadAnnotations,
  saveAnnotations,
  dataPrepareActions,
  saveMethodUpdate,
  updateDatapreparationPurpose,
} from '../../../store/dataPrepare';

import './DataUploadingFinalization.scss';

const {setStatus, setDataPrep} = dataPrepareActions;

type DataUploadingFinalizationProps = {
  projectId: string;
  origin: DataPreparationOrigin;
  dataPreparationContainerId?: string;
  onForward: () => void;
};

export const DataUploadingFinalization = ({
  origin,
  projectId = '',
  dataPreparationContainerId,

  onForward,
}: DataUploadingFinalizationProps) => {
  const intl = useIntl();

  const dispatch = useAppDispatch();
  const [initialized, setInitialized] = useState(false);
  const name = useSelector((state: RootState) => state.dataPrepare.name);
  const type = useSelector((state: RootState) => state.dataPrepare.type);
  const description = useSelector((state: RootState) => state.dataPrepare.description);
  const datasetId = useSelector((state: RootState) => state.dataset.data?.id);
  const dataset = useMemo(() => new DataSet({id: datasetId || ''}), [datasetId]);

  useEffect(
    function initializeFinalizationForDatasetAddRawData() {
      if (!initialized) {
        dataset.addRawData(projectId, dataPreparationContainerId);

        setInitialized(true);
      }
    },
    [initialized, dataset, projectId, dataPreparationContainerId]
  );

  const setReusmeFlagAndStoreProgress = useCallback(
    resourceId => {
      const currentUrl = new URL(window.location.href);

      currentUrl.searchParams.set('resume', 'true');
      window.history.replaceState({}, '', currentUrl.toString());

      if (projectId && resourceId) {
        const purpose =
          type === DataPrepareType.CLASSIFICATION
            ? capitalize(DataPrepareTypeStr.CLASSIFICATION)
            : type === DataPrepareType.DETECTION
            ? capitalize(DataPrepareTypeStr.DETECTION)
            : capitalize(DataPrepareTypeStr.SEGMENTATION);

        dispatch(
          saveMethodUpdate({
            projectId,
            resourceId,
            answers: [
              {key: 'Dataset.Name', value: name || ' '},
              {key: 'Dataset.Description', value: description || ' '},
              {
                key: 'Model.Purpose',
                value: purpose,
              },
            ],
          })
        );

        // TODO do it just once
        dispatch(
          updateDatapreparationPurpose({
            origin,
            purpose,
            projectId,
          })
        );
      }
    },
    [dispatch, projectId, name, description, type, origin]
  );

  const [alreadyFinalized, setAlreadyFinalized] = useState(false);
  const monitorizeAndFinalizeAddDataRaw = useCallback(
    async (status: Status) => {
      if (status.finished) {
        if (!alreadyFinalized) {
          dispatch(removeListener(DataSet.ADD_RAW_DATA_TASK_ID));

          const {body: dataPreparationContainer} = await dataset.finalizeAddRawData(
            projectId
          );
          const dataPrepContainerId = dataPreparationContainer.id;

          const action = await dispatch(loadAnnotations({projectId, origin}));
          const rawAnnotationData = unwrapResult(action);
          await dispatch(
            saveAnnotations({
              projectId,
              resourceId: dataPrepContainerId,
              updatedAnnotations: {
                ...rawAnnotationData,
                info: {...rawAnnotationData.info, type},
              },
            })
          );
          dispatch(setDataPrep({container: dataPreparationContainer, context: origin}));

          setReusmeFlagAndStoreProgress(dataPrepContainerId);

          dispatch(setStatus('IDLE'));

          setAlreadyFinalized(true);
        }

        onForward();
      }
    },
    [
      type,
      origin,
      dataset,
      projectId,
      alreadyFinalized,
      dispatch,
      onForward,
      setReusmeFlagAndStoreProgress,
    ]
  );

  useEffect(() => {
    dispatch(addListener(DataSet.ADD_RAW_DATA_TASK_ID, monitorizeAndFinalizeAddDataRaw));

    return () => {
      dispatch(removeListener(DataSet.ADD_RAW_DATA_TASK_ID));
    };
  }, [dispatch, monitorizeAndFinalizeAddDataRaw]);

  return (
    <div className="data-uploading-finalization">
      <TimerWaiter
        loadingText={intl.formatMessage({
          id: 'dataPrep.importing.rawDAta',
        })}
      />
    </div>
  );
};
